/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 筛选相关工具函数
 */
import { FilterState } from '../types'
import dayjs from 'dayjs'

/**
 * 计算已选成员数量
 */
export const calcPickedMemberNum = (
  filter: FilterState,
  totalMemberCount: number
): number => {
  const userNumInDepartments = filter.departments
    .map((department) => department.num)
    .reduce((acc, cur) => acc + cur, 0)
  const total = filter.departmentUsers.length + userNumInDepartments
  const hasSelection =
    filter.departmentUsers.length > 0 || filter.departments.length > 0

  if (
    (!hasSelection && total === 0) ||
    total === totalMemberCount ||
    filter.departments.some((department) => department.id === '0')
  ) {
    return totalMemberCount
  }
  return total
}

/**
 * 计算成员选择显示值
 */
export const calcMemberPickedValue = (
  filter: FilterState,
  totalMemberCount: number
): string => {
  const pickedMembersNum = calcPickedMemberNum(filter, totalMemberCount)
  if (pickedMembersNum === totalMemberCount) {
    return '筛选人员 全部成员'
  }
  if (pickedMembersNum === 1 && filter.departmentUsers.length > 0) {
    return `<span style="color:#666">筛选人员</span> ${filter.departmentUsers[0].name}`
  }
  return `<span style="color:#666">筛选人员</span> 已选${pickedMembersNum}人`
}

/**
 * 计算水印选择显示值
 */
export const calcWatermarkPickedValue = (
  filter: FilterState,
  totalWatermarksNum: number
): string => {
  if (
    filter.watermarks.length === totalWatermarksNum ||
    filter.watermarks.length === 0
  ) {
    return `筛选水印 全部(${totalWatermarksNum}个)`
  }
  return `<span style="color:#666">筛选水印</span> ${filter.watermarks.length}个水印`
}

/**
 * 计算开启的更多筛选数量
 */
export const calcMoreFilterCount = (filter: FilterState): number => {
  let count = 0
  if (filter.locations && filter.locations.length > 0) {
    count++
  }
  if (filter.mediaType !== null) {
    count++
  }
  if (
    typeof filter.existWatermark === 'number' &&
    filter.existWatermark !== 0
  ) {
    count++
  }
  return count
}

/**
 * 计算「标签筛选」pill 的活跃数量（已选标签数）
 */
export const calcTagFilterCount = (filter: FilterState): number => {
  return filter.labelIDs && filter.labelIDs.length > 0
    ? filter.labelIDs.length
    : 0
}

/**
 * 判断时间筛选是否生效（非默认值）
 */
export const isTimeFilterActive = (
  filter: FilterState,
  defaultTimes?: [dayjs.Dayjs, dayjs.Dayjs] | null
): boolean => {
  if (!filter.times || !filter.times[0] || !filter.times[1]) {
    return false
  }
  if (!defaultTimes) {
    // 如果没有传入默认值，使用初始筛选条件的默认值
    const defaultStart = dayjs().subtract(6, 'months').startOf('day')
    const defaultEnd = dayjs().endOf('day')
    return (
      !filter.times[0].isSame(defaultStart, 'day') ||
      !filter.times[1].isSame(defaultEnd, 'day')
    )
  }
  return (
    !filter.times[0].isSame(defaultTimes[0], 'day') ||
    !filter.times[1].isSame(defaultTimes[1], 'day')
  )
}

/**
 * 判断成员筛选是否生效（非全部成员）
 */
export const isMemberFilterActive = (
  filter: FilterState,
  totalMemberCount: number
): boolean => {
  const pickedMembersNum = calcPickedMemberNum(filter, totalMemberCount)
  return pickedMembersNum !== totalMemberCount
}

/**
 * 判断水印筛选是否生效（非全部水印）
 */
export const isWatermarkFilterActive = (
  filter: FilterState,
  totalWatermarksNum: number
): boolean => {
  return (
    filter.watermarks.length > 0 &&
    filter.watermarks.length !== totalWatermarksNum
  )
}

/**
 * 获取初始筛选条件
 */
export const getInitialFilter = (): FilterState => {
  return {
    searchKeywords: [],
    suggestedSearchWords: [],
    mediaType: null,
    sourceType: null,
    times: [dayjs().subtract(6, 'months').startOf('day'), dayjs().endOf('day')],
    departmentUsers: [],
    departments: [],
    watermarks: [],
    locations: [],
    useSynonym: true,
    useSegment: true,
    useSuggestedSearch: true,
    existWatermark: 0,
    isNlpFilter: false,
    groupByUser: false
  }
}

/**
 * 比较两个筛选条件是否相同
 */
export const compareFilters = (
  filter1: FilterState,
  filter2: FilterState
): boolean => {
  // 简单比较，可以根据需要扩展
  return (
    JSON.stringify(filter1.searchKeywords) ===
      JSON.stringify(filter2.searchKeywords) &&
    filter1.mediaType === filter2.mediaType &&
    filter1.sourceType === filter2.sourceType &&
    JSON.stringify(filter1.times) === JSON.stringify(filter2.times) &&
    JSON.stringify(filter1.departmentUsers) ===
      JSON.stringify(filter2.departmentUsers) &&
    JSON.stringify(filter1.departments) ===
      JSON.stringify(filter2.departments) &&
    JSON.stringify(filter1.watermarks) === JSON.stringify(filter2.watermarks) &&
    JSON.stringify(filter1.locations) === JSON.stringify(filter2.locations) &&
    filter1.groupByUser === filter2.groupByUser
  )
}
